import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { Lock, Mail, ArrowRight, ShieldCheck, Eye, EyeOff, AlertCircle } from "lucide-react";
import { supabase } from "lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { insertLog } from "services/logService";

export default function SignIn() {
  const [email,        setEmail]        = useState(() => localStorage.getItem("core_remembered_email") || "");
  const [password,     setPassword]     = useState("");
  const [rememberMe,   setRememberMe]   = useState(() => localStorage.getItem("core_trust_device") === "true");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [errorMsg,     setErrorMsg]     = useState("");
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    controls.start({ opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } });
    // تحقق إذا في session نشطة → اذهب مباشرة
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate("/admin/default");
    });
  }, []);

  const shake = () => controls.start({ x: [0,-8,8,-8,8,0], transition: { duration: 0.35 } });

  const handleRememberMe = (checked) => {
    setRememberMe(checked);
    if (checked) {
      localStorage.setItem("core_trust_device", "true");
      if (email) localStorage.setItem("core_remembered_email", email);
    } else {
      localStorage.removeItem("core_trust_device");
      localStorage.removeItem("core_remembered_email");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

      if (authError) {
        shake();
        try {
          await insertLog({
            timestamp: new Date().toISOString(),
            actor_name: email, actor_number: "—", actor_role: "guest",
            action: "LOGIN_FAILED",
            details: `Failed login attempt for: ${email}`,
          });
        } catch {}
        throw new Error("Invalid Credentials. Access Refused.");
      }

      const { data: emp } = await supabase
        .from("employees")
        .select("full_name, employee_number, role")
        .eq("id", data.user.id)
        .single();

      // سجّل الدخول في الخلفية بدون await
      insertLog({
        timestamp: new Date().toISOString(),
        actor_name: emp?.full_name || email,
        actor_number: emp?.employee_number || "—",
        actor_role: emp?.role || "—",
        target_id: data.user.id,
        target_name: emp?.full_name || email,
        action: "LOGIN_SUCCESS",
        details: `Signed in | Role: ${emp?.role || "unknown"}`,
      }).catch(() => {});

      // حفظ Remember Me
      if (rememberMe) {
        localStorage.setItem("core_trust_device", "true");
        localStorage.setItem("core_remembered_email", email);
      } else {
        localStorage.removeItem("core_trust_device");
        localStorage.removeItem("core_remembered_email");
      }

      navigate("/admin/default");
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-[#000022] font-sans overflow-hidden" dir="ltr">

      {/* خلفية */}
      <div className="pointer-events-none absolute inset-0 select-none overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]"
          style={{ backgroundImage: `linear-gradient(#0055FF 1px,transparent 1px),linear-gradient(90deg,#0055FF 1px,transparent 1px)`, backgroundSize: "60px 60px" }} />
        <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0055FF]/6 blur-[100px]" />
        <motion.div
          initial={{ top: "-2%" }} animate={{ top: "102%" }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#0055FF]/25 to-transparent"
        />
      </div>

      <motion.div animate={controls} initial={{ opacity: 0, y: 16 }} className="relative z-10 w-full max-w-[380px] p-4">

        {/* Logo */}
        <div className="mb-8 text-center">
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="mx-auto mb-4">
            <ShieldCheck className="mx-auto h-9 w-9 text-[#0055FF]" strokeWidth={0.75} />
          </motion.div>
          <h1 className="text-xl font-light uppercase tracking-[0.5em] text-white">
            CORE <span className="opacity-20">Portal</span>
          </h1>
          <p className="mt-1 text-[9px] uppercase tracking-[0.3em] text-white/20">Professional Management System</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 backdrop-blur-3xl shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
          {errorMsg && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
              className="mb-6 flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/8 p-3 text-[10px] text-red-400 uppercase tracking-wider">
              <AlertCircle size={13} className="shrink-0" /> {errorMsg}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Email */}
            <div className="space-y-2">
              <label className="block text-[9px] font-medium uppercase tracking-[0.25em] text-white/40">
                Email Address
              </label>
              <div className="relative group">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0055FF] transition-colors" size={14} strokeWidth={1.5} />
                <input
                  required type="email" value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (rememberMe) localStorage.setItem("core_remembered_email", e.target.value);
                  }}
                  placeholder="name@coreprofessional.sa"
                  className="w-full border-b border-white/10 bg-transparent py-2.5 pl-7 text-sm font-light text-white outline-none transition-all placeholder:text-white/10 focus:border-[#0055FF]/60"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-[9px] font-medium uppercase tracking-[0.25em] text-white/40">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0055FF] transition-colors" size={14} strokeWidth={1.5} />
                <input
                  required type={showPassword ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border-b border-white/10 bg-transparent py-2.5 pl-7 pr-8 text-sm font-light text-white outline-none transition-all placeholder:text-white/20 focus:border-[#0055FF]/60"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/60 transition-colors">
                  {showPassword ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <label className="group flex cursor-pointer items-center gap-3">
                <div className="relative flex h-4 w-4 items-center justify-center rounded border border-white/15 bg-white/5 transition-all group-hover:border-[#0055FF]/50">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => handleRememberMe(e.target.checked)}
                    className="peer absolute h-full w-full opacity-0 cursor-pointer"
                  />
                  {rememberMe && (
                    <div className="h-2 w-2 rounded-sm bg-[#0055FF] shadow-[0_0_6px_#0055FF]" />
                  )}
                </div>
                <span className="text-[9px] uppercase tracking-[0.25em] text-white/30 group-hover:text-white/50 transition-colors">
                  Trust this device
                </span>
              </label>
            </div>

            {/* Submit */}
            <button
              disabled={isLoading}
              className="relative flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] py-4 text-[10px] font-medium uppercase tracking-[0.5em] text-white transition-all hover:bg-[#0055FF] hover:border-[#0055FF] hover:shadow-[0_0_30px_rgba(0,85,255,0.35)] active:scale-[0.98] disabled:opacity-50 group">
              <span className="flex items-center gap-3">
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 animate-spin rounded-full border border-white/40 border-t-white" />
                    Authenticating...
                  </span>
                ) : (
                  <>Sign In <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" /></>
                )}
              </span>
            </button>
          </form>
        </div>

        <p className="mt-8 text-center text-[8px] uppercase tracking-[0.4em] text-white/10">
          CORE Professional © {new Date().getFullYear()}
        </p>
      </motion.div>
    </div>
  );
}